import React from 'react';
import PropTypes from 'prop-types';
import DesignSelect from './option_defaults/design_select';
import OptionDefaultsApi from './option_defaults/api';
import Options from './options';
import PropertySelect from './option_defaults/property_select';
import ReadOnlyPreview from './read_only_preview';

import './option_defaults/styles.scss';
import { take } from 'lodash';

class OptionDefaults extends React.Component {
  static propTypes = {
    brokers: PropTypes.array.isRequired,
    designOverrides: PropTypes.arrayOf(PropTypes.shape({
      designId: PropTypes.number.isRequired,
      id: PropTypes.number.isRequired,
      optionDefaults: PropTypes.object.isRequired
    })).isRequired,
    designs: PropTypes.array.isRequired,
    paths: PropTypes.shape({
      designOverride: PropTypes.string.isRequired,
      preview: PropTypes.string.isRequired
    }).isRequired,
    properties: PropTypes.array.isRequired
  }

  constructor(props) {
    super(props);

    const designOverridesByDesignId = {};
    for (const designOverride of props.designOverrides) {
      designOverridesByDesignId[designOverride.designId] = designOverride;
    }

    const design = props.designs[0];

    this.state = {
      design,
      designOverridesByDesignId,
      options: design.designVersion.options,
      optionValues: {},
      previewHtml: '',
      property: props.properties[0],
      properties: take(props.properties, 3)
    };

    this.api = new OptionDefaultsApi(props.paths.designOverride, props.paths.preview);
  }

  componentDidMount() {
    this.updatePreview();
  }

  copyDesignOverrideForDesign(design) {
    if (this.state.designOverridesByDesignId[design.id]) {
      return JSON.parse(JSON.stringify(this.state.designOverridesByDesignId[design.id]));
    } else {
      return {
        designId: design.id,
        optionDefaults: {}
      };
    }
  }

  buildStateChangeForDesignOverride(designOverride) {
    return {
      designOverridesByDesignId: {
        ...this.state.designOverridesByDesignId,
        [designOverride.designId]: designOverride
      }
    };
  }

  getCurrentDesignOverride() {
    const { design } = this.state;
    return this.state.designOverridesByDesignId[design.id];
  }

  saveCurrentDesignOverride = () => {
    this.api.saveDesignOverride(this.getCurrentDesignOverride()).then((response) => {
      this.setState(({ design }) => {
        const designOverride = this.copyDesignOverrideForDesign(design);
        designOverride.id = response.data.id;
        return this.buildStateChangeForDesignOverride(designOverride);
      });
    });
  }

  deleteCurrentDesignOverride = () => {
    this.api.deleteDesignOverride(this.getCurrentDesignOverride());

    this.setState(({ design }) => {
      const designOverridesByDesignId = { ...this.state.designOverridesByDesignId };
      delete designOverridesByDesignId[design.id];
      return { designOverridesByDesignId };
    });
  }

  updatePreview = () => {
    this.setState({ updating: true });

    const { design, optionValues, property, properties } = this.state;
    this.api.getPreview(design, optionValues, property, properties, this.props.brokers).then((response) => {
      this.setState({
        previewHtml: response.data.html,
        options: response.data.options,
        optionValues: response.data.option_values,
        updating: false
      });
    });
  }

  handleDesignChange = (design) => {
    this.setState({
      design,
      options: design.designVersion.options
    }, this.updatePreview);
  }

  handlePropertyChange = (property) => {
    this.setState({ property }, this.updatePreview);
  }

  handlePropertiesChange = (properties) => {
    this.setState({ properties }, this.updatePreview);
  }

  handleOptionValuesChange = (changes) => {
    this.setState(({ design, optionValues }) => {
      const designOverride = this.copyDesignOverrideForDesign(design);

      for (const id in changes) {
        designOverride.optionDefaults[id] = changes[id];
      }

      return {
        ...this.buildStateChangeForDesignOverride(designOverride),
        optionValues: { ...optionValues, ...changes }
      };
    }, () => {
      this.saveCurrentDesignOverride();
      this.updatePreview();
    });
  }

  handleOverrideOptionDefault = (option) => {
    this.setState(({ design, optionValues }) => {
      const designOverride = this.copyDesignOverrideForDesign(design);
      designOverride.optionDefaults[option.id] = optionValues[option.id];
      return this.buildStateChangeForDesignOverride(designOverride);
    }, this.saveCurrentDesignOverride);
  }

  handleUseOptionDefault = (option) => {
    let valueChanged;

    this.setState(({ design, optionValues }) => {
      const designOverride = this.copyDesignOverrideForDesign(design);
      delete designOverride.optionDefaults[option.id];

      const defaultValue = option.default.evaluated;
      valueChanged = optionValues[option.id] !== defaultValue;

      return {
        ...this.buildStateChangeForDesignOverride(designOverride),
        optionValues: { ...optionValues, [option.id]: defaultValue }
      };
    }, () => {
      if (Object.keys(this.getCurrentDesignOverride().optionDefaults).length === 0) {
        this.deleteCurrentDesignOverride();
      } else {
        this.saveCurrentDesignOverride();
      }

      if (valueChanged) {
        this.updatePreview();
      }
    });
  }

  render() {
    return (
      <div className="design-option-defaults">
        <div className="d-flex gap-5 mt-5 mb-2">
          {this.renderControls()}
        </div>

        <div className="body">
          <div className="sidebar">
            {this.renderOptions()}
          </div>

          <ReadOnlyPreview
            html={this.state.previewHtml}
            onlyWeb={true}
            updating={this.state.updating}
          />
        </div>
      </div>
    );
  }

  renderControls() {
    return <React.Fragment>
      <DesignSelect
        design={this.state.design}
        designs={this.props.designs}
        onChange={this.handleDesignChange}
      />

      <PropertySelect
        allProperties={this.props.properties}
        design={this.state.design}
        properties={this.state.properties}
        property={this.state.property}
        onPropertiesChange={this.handlePropertiesChange}
        onPropertyChange={this.handlePropertyChange}
      />
    </React.Fragment>;
  }

  renderOptions() {
    const designOverride = this.state.designOverridesByDesignId[this.state.design.id] || {
      optionDefaults: {}
    };

    return (
      <Options
        defaultOverrides={designOverride.optionDefaults}
        options={this.state.options.filter(option => !option.hidden.evaluated)}
        values={this.state.optionValues}
        onOverrideOptionDefault={this.handleOverrideOptionDefault}
        onUseOptionDefault={this.handleUseOptionDefault}
        onValuesChange={this.handleOptionValuesChange}
      />
    );
  }
}

export default OptionDefaults;
